import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import { createTheme, ThemeProvider,  withStyles } from '@material-ui/core/styles';
import { Button, Dialog, DialogContent, DialogTitle, DialogActions,
  Typography, TextField, Checkbox
} from '@material-ui/core';
import { addAlert } from '../../actions/alerts.action';
import RatingSlider from './DialogComponents/RatingSlider';
import { createObjectiveRate, updateObjectiveRate } from '../../api/objective/objectiveRate.api';
import { useMutation } from 'react-query';

const EvaluateDialog = props => {
  const {
    open,
    handleClose,
    nameOfCriteria,
    isOnlyText,
    rateOfCriteria,
    employeeId,
    onSubmitSuccess
  } = props;

  const [ rating, setRating ] = useState(rateOfCriteria);
  const [ sliderValue, setSliderValue ] = useState(rateOfCriteria.value);
  const [ isOverPeformed, setOverPreformed ] = useState(false);

  const handleCheckBox = event => {
    setOverPreformed(event.target.checked);
    if (!event.target.checked && sliderValue > 100) setSliderValue(100);
  };

  useEffect(() => {
    let { value } = rateOfCriteria;
    if (value && !sliderValue && sliderValue !== 0) setSliderValue(value);
  },[ rateOfCriteria, sliderValue ]);

  useEffect(() => {
    let { value } = rateOfCriteria;
    setRating(rateOfCriteria);
    if (value > 100) setOverPreformed(true);
    if (value <= 100) setOverPreformed(false);
  }, [ rateOfCriteria ]);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleOnChangeText = event => setRating({
    ...rating,
    text: event.target.value
  });

  const handleOnChangeSlideValue =  (event, value) => setSliderValue(value);

  const handleCancel = () => {
    handleClose();
    setRating(rateOfCriteria);
  };

  const saveObjectiveRateMutation = useMutation(
    async () => {
      const params = {
        employeeId,
        objectiveId: rating.objective_id,
        objectiveRate: {
          ...rating,
          value: sliderValue ,
          employeeId
        }
      };

      return rating.id ?
        await updateObjectiveRate(params) :
        await createObjectiveRate(params);
    }, {
      onSuccess: () => {
        onSubmitSuccess?.();
        handleClose();
      },
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  const handleSubmit = () => {
    if (!isOnlyText && typeof sliderValue === 'undefined') {
      dispatch(addAlert({ message: `Kérjük adjon meg százalékos értékelést`, severity: 'error' }));
      return;
    }

    saveObjectiveRateMutation.mutate();
  };

  const useStyles = makeStyles({
    centered: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    value: {
      fontWeight: 'bold',
      fontSize: '48px',
      lineHeight: '56px',
      color: '#5DCDE7'
    },
    title: {
      backgroundColor: '#fff',
      color: 'inherit'
    },
    buttons: {
      width: '95%',
      margin: '0 auto',
      justifyContent: 'start',
      paddingBottom: '24px'
    },
    checkbox: {
      width: '100%',
      justifyContent: 'start'
    },
    checkboxtext: {
      color: isOverPeformed ? '#58AF61' : '#000000',
      marginLeft: '0 !important'
    },
    valueField: {
      maxWidth: '120px',
      width: '100%'
    },
    saveBtn: {
      backgroundColor: '#5DCDE7',
      '&:hover': {
        backgroundColor: '#528CA1'
      }
    },
    backBtn: {
      color: '#5DCDE7',
      borderColor: '#5DCDE7',
      '&:hover': {
        borderColor: '#5DCDE7'
      }
    }
  });

  const marks = [
    {
      value: 0,
      label: '0%'
    },
    {
      value: 100,
      label: '100%'
    },
    {
      value: (rating.criteria.criteria_rate_upper_limit && isOverPeformed) ?
        rating.criteria.criteria_rate_upper_limit : '',
      label: (rating.criteria.criteria_rate_upper_limit && isOverPeformed) ?
        ` ${ rating.criteria.criteria_rate_upper_limit }%` : ''
    }
  ];

  const TextFieldTheme = createTheme({
    overrides: {
      MuiOutlinedInput: {
        root: {
          '&$focused $notchedOutline': {
            'borderColor': '#5DCDE7'
          }
        }
      }
    }
  });

  const GreenCheckbox = withStyles({
    root: {
      color: '#000000',
      '&$checked': {
        color: '#58AF61'
      },
      '&:hover': {
        backgroundColor: 'transparent'
      }
    },
    checked: {}
  })(props => <Checkbox color="default" { ...props } />);

  const sliderMaxValue =  (rating.criteria.criteria_rate_upper_limit > 100 && isOverPeformed) ?
    parseInt(rating.criteria.criteria_rate_upper_limit) : 100;

  const classes = useStyles();

  return (
    <Dialog
      open={ open }
      aria-labelledby='form-dialog-title'
      fullWidth={ true }
      maxWidth={ 'md' }
    >
      <DialogTitle
        className={ classes.title }
      >
        { nameOfCriteria }
      </DialogTitle>
      <DialogContent className={ classes.centered }>
        { !isOnlyText &&
        <>
          <Typography variant="h5" className={ classes.value }>
            { `${sliderValue ?? ''}%` }
          </Typography>
          <RatingSlider
            value={ parseInt(sliderValue) || 0 }
            max= { sliderMaxValue }
            marks={ marks }
            onChange={ handleOnChangeSlideValue }
          />
        </>
        }
        { !isOnlyText && rating.criteria.criteria_rate_upper_limit > 100 &&
          <DialogActions className={ classes.checkbox }>
            <GreenCheckbox
              checked={ isOverPeformed }
              onChange={ handleCheckBox }
            />
            <span className={ classes.checkboxtext }>
              { t('objective.evaluation.dialog.checkbox.text') }
            </span>
          </DialogActions>
        }
        <ThemeProvider theme={ TextFieldTheme }>
          <TextField
            value={ rating.text }
            variant="outlined"
            placeholder={ t('objective.evaluation.dialog.placeholder.text') }
            multiline
            minRows={ 10 }
            maxRows={ 10 }
            fullWidth
            onChange={ handleOnChangeText }
            InputProps={ {
              classes: {
                root: classes.texfiledRoot
              }
            } }
          />
        </ThemeProvider>
      </DialogContent >
      <DialogActions className={ classes.buttons }>
        <Button
          onClick={ handleSubmit }
          className={ classes.saveBtn }
          variant="contained"
          color="primary"
        >
          <div>{t('objective.evaluation.dialog.save')}</div>
        </Button>
        <Button
          onClick={ handleCancel }
          className={ classes.backBtn }
          variant="outlined"
          color="primary"
        >
          <div>{t('objective.evaluation.dialog.cancel')}</div>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvaluateDialog;